/**
 * Security Error Handler for localStorage and other browser security restrictions
 */

/**
 * Sets up global error handlers for SecurityError
 */
export const setupSecurityErrorHandler = () => {
    // Handle unhandled promise rejections that might be SecurityErrors
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason && event.reason.name === 'SecurityError') {
            console.warn('SecurityError caught:', event.reason.message);
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(event.reason, {
                    tags: {
                        errorType: 'SECURITY_ERROR',
                        context: 'UNHANDLED_REJECTION'
                    }
                });
            }
            
            // Prevent the default behavior
            event.preventDefault();
        }
    });

    // Handle general errors
    window.addEventListener('error', (event) => {
        if (event.error && event.error.name === 'SecurityError') {
            console.warn('SecurityError caught:', event.error.message);
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(event.error, {
                    tags: {
                        errorType: 'SECURITY_ERROR',
                        context: 'GENERAL_ERROR'
                    }
                });
            }
        }
    });
};

/**
 * Wraps a function to handle SecurityError gracefully
 * @param {Function} fn - Function to wrap
 * @param {any} fallbackValue - Value to return if SecurityError occurs
 * @param {string} context - Context for logging
 * @returns {Function} - Wrapped function
 */
export const withSecurityErrorHandling = (fn, fallbackValue = null, context = 'Unknown') => {
    return (...args) => {
        try {
            return fn(...args);
        } catch (error) {
            if (error.name === 'SecurityError') {
                console.warn(`SecurityError in ${context}:`, error.message);
                
                // Log to Sentry if available
                if (window.Sentry) {
                    window.Sentry.captureException(error, {
                        tags: {
                            errorType: 'SECURITY_ERROR',
                            context: context
                        }
                    });
                }
                
                return fallbackValue;
            }
            throw error;
        }
    };
};

/**
 * Creates a safe wrapper for localStorage operations
 * @param {Object} storage - Storage object (localStorage, sessionStorage, etc.)
 * @param {string} context - Context for logging
 * @returns {Object} - Safe storage wrapper
 */
export const createSafeStorageWrapper = (storage, context = 'Storage') => {
    const safeGetItem = withSecurityErrorHandling(
        (key) => storage.getItem(key),
        null,
        `${context}.getItem`
    );
    
    const safeSetItem = withSecurityErrorHandling(
        (key, value) => storage.setItem(key, value),
        false,
        `${context}.setItem`
    );
    
    const safeRemoveItem = withSecurityErrorHandling(
        (key) => storage.removeItem(key),
        false,
        `${context}.removeItem`
    );
    
    const safeClear = withSecurityErrorHandling(
        () => storage.clear(),
        false,
        `${context}.clear`
    );
    
    return {
        getItem: safeGetItem,
        setItem: safeSetItem,
        removeItem: safeRemoveItem,
        clear: safeClear,
        length: storage.length || 0,
        key: withSecurityErrorHandling(
            (index) => storage.key(index),
            null,
            `${context}.key`
        )
    };
};

/**
 * Detects if the browser is in private/incognito mode
 * @returns {Promise<boolean>} - True if in private mode
 */
export const detectPrivateMode = () => {
    return new Promise((resolve) => {
        try {
            // Test localStorage
            const testKey = '__private_mode_test__';
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);
            
            // Test sessionStorage
            sessionStorage.setItem(testKey, 'test');
            sessionStorage.removeItem(testKey);
            
            resolve(false); // Not in private mode
        } catch (error) {
            if (error.name === 'SecurityError') {
                resolve(true); // Likely in private mode
            } else {
                resolve(false); // Other error, not private mode
            }
        }
    });
};

/**
 * Gets browser security status
 * @returns {Object} - Security status information
 */
export const getSecurityStatus = () => {
    const status = {
        localStorage: false,
        sessionStorage: false,
        cookies: false,
        isPrivateMode: false,
        userAgent: navigator.userAgent,
        platform: navigator.platform
    };
    
    try {
        // Test localStorage
        const testKey = '__security_test__';
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);
        status.localStorage = true;
    } catch (error) {
        if (error.name === 'SecurityError') {
            status.isPrivateMode = true;
        }
    }
    
    try {
        // Test sessionStorage
        const testKey = '__security_test__';
        sessionStorage.setItem(testKey, 'test');
        sessionStorage.removeItem(testKey);
        status.sessionStorage = true;
    } catch (error) {
        if (error.name === 'SecurityError') {
            status.isPrivateMode = true;
        }
    }
    
    try {
        // Test cookies
        document.cookie = '__security_test__=test';
        status.cookies = document.cookie.includes('__security_test__');
        document.cookie = '__security_test__=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    } catch (error) {
        // Cookie test failed
    }
    
    return status;
};

export default {
    setupSecurityErrorHandler,
    withSecurityErrorHandling,
    createSafeStorageWrapper,
    detectPrivateMode,
    getSecurityStatus
};
